/**
 * --------------------------------------------------------------------------
 * Bootstrap (v4.6.2): collapse.js
 * Licensed under MIT (https://github.com/twbs/bootstrap/blob/main/LICENSE)
 * --------------------------------------------------------------------------
 */

import Util from '../BootstrapUtil/bootstrap-util'

/**
 * Constants
 */

const NAME = 'collapse'
const VERSION = '4.6.2'
const DATA_KEY = 'bs.collapse'
const EVENT_KEY = `.${DATA_KEY}`
const DATA_API_KEY = '.data-api'

const CLASS_NAME_SHOW = 'show'
const CLASS_NAME_COLLAPSE = 'collapse'
const CLASS_NAME_COLLAPSING = 'collapsing'
const CLASS_NAME_COLLAPSED = 'collapsed'

const DIMENSION_WIDTH = 'width'
const DIMENSION_HEIGHT = 'height'

const EVENT_SHOW = `show${EVENT_KEY}`
const EVENT_SHOWN = `shown${EVENT_KEY}`
const EVENT_HIDE = `hide${EVENT_KEY}`
const EVENT_HIDDEN = `hidden${EVENT_KEY}`
const EVENT_CLICK_DATA_API = `click${EVENT_KEY}${DATA_API_KEY}`

const TRANSITION_END = 'transitionend'
const SELECTOR_ACTIVES = '.show, .collapsing'
const SELECTOR_DATA_TOGGLE = '[data-toggle="collapse"]'

const Default = {
  toggle: true,
  parent: ''
}

const DefaultType = {
  toggle: 'boolean',
  parent: '(string|element)'
}

/**
 * Class definition
 */

class Collapse {
  static _instances = new WeakMap()

  constructor(element, config) {
    this._isTransitioning = false
    this._element = element
    this._config = this._getConfig(config)
    this._triggerArray = [].slice.call(document.querySelectorAll(
      `[data-toggle="collapse"][href="#${element.id}"],` +
      `[data-toggle="collapse"][data-target="#${element.id}"]`
    ))

    const toggleList = [].slice.call(document.querySelectorAll(SELECTOR_DATA_TOGGLE))
    for (let i = 0, len = toggleList.length; i < len; i++) {
      const elem = toggleList[i]
      const selector = Util.getSelectorFromElement(elem)
      const filterElement = [].slice.call(document.querySelectorAll(selector))
        .filter(foundElem => foundElem === element)

      if (selector !== null && filterElement.length > 0) {
        this._selector = selector
        this._triggerArray.push(elem)
      }
    }

    this._parent = this._config.parent ? this._getParent() : null

    if (!this._config.parent) {
      this._addAriaAndCollapsedClass(this._element, this._triggerArray)
    }

    if (this._config.toggle) {
      this.toggle()
    }

    Collapse._instances.set(this._element, this)
  }

  // Getters
  static get VERSION() {
    return VERSION
  }

  static get Default() {
    return Default
  }

  // Public
  toggle() {
    if (this._element.classList.contains(CLASS_NAME_SHOW)) {
      this.hide()
    } else {
      this.show()
    }
  }

  show() {
    if (this._isTransitioning ||
      this._element.classList.contains(CLASS_NAME_SHOW)) {
      return
    }

    let actives
    let activesData

    if (this._parent) {
      actives = [].slice.call(this._parent.querySelectorAll(SELECTOR_ACTIVES))
        .filter(elem => {
          if (typeof this._config.parent === 'string') {
            return elem.getAttribute('data-parent') === this._config.parent
          }

          return elem.classList.contains(CLASS_NAME_COLLAPSE)
        })

      if (actives.length === 0) {
        actives = null
      }
    }

    if (actives) {
      const activeElements = this._selector
        ? actives.filter(element => element !== this._element)
        : actives
      activesData = activeElements
        .map(element => Collapse.getInstance(element))
        .find(Boolean)
      if (activesData && activesData._isTransitioning) {
        return
      }
    }

    const startEvent = Collapse._triggerEvent(this._element, EVENT_SHOW)
    if (startEvent.defaultPrevented) {
      return
    }

    if (actives) {
      const activeElements = this._selector
        ? actives.filter(element => element !== this._element)
        : actives
      activeElements.forEach(element => {
        const instance = Collapse.getOrCreateInstance(element, { toggle: false })
        instance.hide()
      })
      if (!activesData) {
        actives.forEach(element => {
          Collapse._instances.delete(element)
        })
      }
    }

    const dimension = this._getDimension()

    this._element.classList.remove(CLASS_NAME_COLLAPSE)
    this._element.classList.add(CLASS_NAME_COLLAPSING)

    this._element.style[dimension] = 0

    if (this._triggerArray.length) {
      this._triggerArray.forEach(trigger => {
        trigger.classList.remove(CLASS_NAME_COLLAPSED)
        trigger.setAttribute('aria-expanded', true)
      })
    }

    this.setTransitioning(true)

    const complete = () => {
      this._element.classList.remove(CLASS_NAME_COLLAPSING)
      this._element.classList.add(CLASS_NAME_COLLAPSE, CLASS_NAME_SHOW)

      this._element.style[dimension] = ''

      this.setTransitioning(false)

      Collapse._triggerEvent(this._element, EVENT_SHOWN)
    }

    const capitalizedDimension = dimension[0].toUpperCase() + dimension.slice(1)
    const scrollSize = `scroll${capitalizedDimension}`
    const transitionDuration = Util.getTransitionDurationFromElement(this._element)

    Collapse._queueCallback(complete, this._element, transitionDuration)

    this._element.style[dimension] = `${this._element[scrollSize]}px`
  }

  hide() {
    if (this._isTransitioning ||
      !this._element.classList.contains(CLASS_NAME_SHOW)) {
      return
    }

    const startEvent = Collapse._triggerEvent(this._element, EVENT_HIDE)
    if (startEvent.defaultPrevented) {
      return
    }

    const dimension = this._getDimension()

    this._element.style[dimension] = `${this._element.getBoundingClientRect()[dimension]}px`

    Util.reflow(this._element)

    this._element.classList.add(CLASS_NAME_COLLAPSING)
    this._element.classList.remove(CLASS_NAME_COLLAPSE, CLASS_NAME_SHOW)

    const triggerArrayLength = this._triggerArray.length
    if (triggerArrayLength > 0) {
      for (let i = 0; i < triggerArrayLength; i++) {
        const trigger = this._triggerArray[i]
        const selector = Util.getSelectorFromElement(trigger)

        if (selector !== null) {
          const elems = [].slice.call(document.querySelectorAll(selector))
          const hasShown = elems.some(element => element.classList.contains(CLASS_NAME_SHOW))
          if (!hasShown) {
            trigger.classList.add(CLASS_NAME_COLLAPSED)
            trigger.setAttribute('aria-expanded', false)
          }
        }
      }
    }

    this.setTransitioning(true)

    const complete = () => {
      this.setTransitioning(false)
      this._element.classList.remove(CLASS_NAME_COLLAPSING)
      this._element.classList.add(CLASS_NAME_COLLAPSE)
      Collapse._triggerEvent(this._element, EVENT_HIDDEN)
    }

    this._element.style[dimension] = ''
    const transitionDuration = Util.getTransitionDurationFromElement(this._element)

    Collapse._queueCallback(complete, this._element, transitionDuration)
  }

  setTransitioning(isTransitioning) {
    this._isTransitioning = isTransitioning
  }

  dispose() {
    Collapse._instances.delete(this._element)

    this._config = null
    this._parent = null
    this._element = null
    this._triggerArray = null
    this._isTransitioning = null
  }

  // Private
  _getConfig(config) {
    config = {
      ...Default,
      ...config
    }
    config.toggle = Boolean(config.toggle) // Coerce string values
    Util.typeCheckConfig(NAME, config, DefaultType)
    return config
  }

  _getDimension() {
    const hasWidth = this._element.classList.contains(DIMENSION_WIDTH)
    return hasWidth ? DIMENSION_WIDTH : DIMENSION_HEIGHT
  }

  _getParent() {
    let parent

    if (Util.isElement(this._config.parent)) {
      parent = this._config.parent

      // It's a jQuery object
      if (typeof this._config.parent.jquery !== 'undefined') {
        parent = this._config.parent[0]
      }
    } else {
      parent = document.querySelector(this._config.parent)
    }

    const selector = `[data-toggle="collapse"][data-parent="${this._config.parent}"]`
    const children = [].slice.call(parent.querySelectorAll(selector))

    children.forEach(element => {
      this._addAriaAndCollapsedClass(
        Collapse._getTargetFromElement(element),
        [element]
      )
    })

    return parent
  }

  _addAriaAndCollapsedClass(element, triggerArray) {
    const isOpen = element.classList.contains(CLASS_NAME_SHOW)

    if (triggerArray.length) {
      triggerArray.forEach(trigger => {
        trigger.classList.toggle(CLASS_NAME_COLLAPSED, !isOpen)
        trigger.setAttribute('aria-expanded', isOpen)
      })
    }
  }

  // Static
  static getInstance(element) {
    return Collapse._instances.get(element) || null
  }

  static getOrCreateInstance(element, config = {}) {
    return this.getInstance(element) || new this(element, config)
  }

  static _triggerEvent(element, eventName) {
    const event = new CustomEvent(eventName, {
      bubbles: true,
      cancelable: true
    })

    element.dispatchEvent(event)

    const jQueryRef = window.jQuery
    if (typeof jQueryRef !== 'undefined') {
      const jqEvent = jQueryRef.Event(eventName)
      jQueryRef(element).trigger(jqEvent)
      if (jqEvent.isDefaultPrevented()) {
        event.preventDefault()
      }
    }

    return event
  }

  static _queueCallback(callback, element, transitionDuration) {
    let called = false
    const duration = transitionDuration || 0

    const handler = event => {
      if (event.target !== element) {
        return
      }
      called = true
      element.removeEventListener(TRANSITION_END, handler)
      callback()
    }

    element.addEventListener(TRANSITION_END, handler)

    window.setTimeout(() => {
      if (!called) {
        Util.triggerTransitionEnd(element)
      }
    }, duration + 5)
  }

  static _getTargetFromElement(element) {
    const selector = Util.getSelectorFromElement(element)
    return selector ? document.querySelector(selector) : null
  }

  static _getDataAttributes(element) {
    const data = {}
    if (typeof element.dataset.toggle !== 'undefined') {
      data.toggle = element.dataset.toggle !== 'false'
    }
    if (typeof element.dataset.parent !== 'undefined') {
      data.parent = element.dataset.parent
    }
    return data
  }

  static interface(elements, config) {
    elements.forEach(element => {
      let data = Collapse.getInstance(element)
      const elementData = Collapse._getDataAttributes(element)
      const _config = {
        ...Default,
        ...elementData,
        ...(typeof config === 'object' && config ? config : {})
      }

      if (!data && _config.toggle && typeof config === 'string' && /show|hide/.test(config)) {
        _config.toggle = false
      }

      if (!data) {
        data = new Collapse(element, _config)
      }

      if (typeof config === 'string') {
        if (typeof data[config] === 'undefined') {
          throw new TypeError(`No method named "${config}"`)
        }
        data[config]()
      }
    })
  }
}

/**
 * Data API implementation
 */

document.addEventListener('click', function (event) {
  const trigger = event.target.closest(SELECTOR_DATA_TOGGLE)
  if (!trigger) {
    return
  }

  // preventDefault only for <a> elements (which change the URL) not inside the collapsible element
  if (trigger.tagName === 'A') {
    event.preventDefault()
  }

  const selector = Util.getSelectorFromElement(trigger)
  if (!selector) {
    return
  }

  const selectors = [].slice.call(document.querySelectorAll(selector))

  selectors.forEach(target => {
    const data = Collapse.getInstance(target)
    const config = data ? 'toggle' : Collapse._getDataAttributes(trigger)
    Collapse.interface([target], config)
  })
})

/**
 * Optional jQuery bridge
 */
if (typeof window.jQuery !== 'undefined') {
  const $ = window.jQuery
  const JQUERY_NO_CONFLICT = $.fn[NAME]
  $.fn[NAME] = function (config) {
    return Collapse.interface([].slice.call(this), config)
  }
  $.fn[NAME].Constructor = Collapse
  $.fn[NAME].noConflict = () => {
    $.fn[NAME] = JQUERY_NO_CONFLICT
    return $.fn[NAME]
  }
}

export default Collapse
